import React, { useState, useEffect, useRef, useCallback } from 'react';
import supabase from '../../config/supabase';
import { resolveVendorByNameOrAlias } from '../../utils/vendorResolution';
import { saveVendorContactAndPropagate } from '../../services/purchaseOrderService';
import useHideMobileNav from '../../utils/useHideMobileNav';
import {
  parseFeeAmountInput,
  feePercentToFraction,
  feeFractionToPercent,
  mergeFeeProvenance,
  pickChangedFeeValues,
  isMissingFeeColumnError,
} from '../VendorEditor';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isValidVendorId(v) {
  if (v == null) return false;
  if (UUID_RE.test(String(v))) return true;
  const n = Number(v);
  return Number.isFinite(n) && n > 0;
}

// The contact columns this modal has always written. Kept as its own list
// because it is also the fallback select for a store that is behind on the
// migrations that added the ordering/fee columns below.
const CONTACT_SELECT = (
  'id, display_name, store_id, status,'
  + ' contact_name, contact_email, contact_phone,'
  + ' contact2_name, contact2_email, contact2_phone,'
  + ' general_email, general_phone,'
  + ' send_orders_to_contact_email, send_orders_to_general_email'
);

// Everything the vendor-settings form edits. Tried first; a store missing any
// of these columns falls back to CONTACT_SELECT and the extra sections are
// hidden rather than the whole modal failing to open.
const SETTINGS_SELECT = (
  `${CONTACT_SELECT},`
  + ' account_number, payment_terms, address, website, notes,'
  + ' fulfillment_type, ordering_method, lead_days, cutoff_time, delivery_days,'
  + ' minimum_order_amount, below_minimum_fee, delivery_fee, free_delivery_threshold,'
  + ' fuel_surcharge_amount, sales_tax_rate, fee_provenance'
);

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

const EMPTY_FORM = {
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  contact2_name: '',
  contact2_email: '',
  contact2_phone: '',
  general_email: '',
  general_phone: '',
  send_orders_to_contact_email: true,
  send_orders_to_general_email: false,
  fulfillment_type: 'delivery',
  ordering_method: 'email',
  lead_days: '',
  cutoff_time: '',
  delivery_days: '',
  minimum_order_amount: '',
  below_minimum_fee: '',
  delivery_fee: '',
  free_delivery_threshold: '',
  fuel_surcharge_amount: '',
  sales_tax_rate: '',
  account_number: '',
  payment_terms: '',
  address: '',
  website: '',
  notes: '',
};

const S = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 10100,
    background: 'rgba(43,29,20,0.72)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    paddingTop: 'calc(env(safe-area-inset-top, 0px) + 20px)',
    paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)',
    paddingLeft: 16, paddingRight: 16,
  },
  modal: {
    background: '#fff', borderRadius: 16, width: '100%', maxWidth: 520,
    maxHeight: 'calc(90vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 8px 48px rgba(43,29,20,0.28)', overflow: 'hidden',
    outline: 'none',
  },
  header: {
    padding: '18px 20px 14px', borderBottom: '1px solid #e8e0d5',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10,
  },
  title: { fontSize: 17, fontWeight: 800, color: '#2b1d14', margin: 0, lineHeight: 1.3 },
  subtitle: { fontSize: 12, color: '#7b6b61', marginTop: 3 },
  closeBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: 22, color: '#5A4A42', padding: '0 4px', lineHeight: 1,
  },
  body: { flex: 1, overflowY: 'auto', padding: '16px 20px', WebkitOverflowScrolling: 'touch' },
  footer: {
    padding: '14px 20px', borderTop: '1px solid #e8e0d5',
    display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap',
  },
  fieldGroup: { marginBottom: 14 },
  fieldRow: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  fieldCol: { flex: '1 1 150px', minWidth: 0, marginBottom: 14 },
  label: { display: 'block', fontSize: 12, fontWeight: 700, color: '#5A4A42', marginBottom: 5 },
  hint: { fontSize: 11, color: '#8B7B73', marginTop: 4 },
  input: {
    width: '100%', boxSizing: 'border-box', padding: '9px 12px',
    borderRadius: 8, border: '1.5px solid #d5c9c0', fontSize: 14,
    color: '#2b1d14', background: '#fff', outline: 'none', fontFamily: 'inherit',
  },
  textarea: {
    width: '100%', boxSizing: 'border-box', padding: '9px 12px',
    borderRadius: 8, border: '1.5px solid #d5c9c0', fontSize: 14,
    color: '#2b1d14', background: '#fff', outline: 'none', fontFamily: 'inherit',
    resize: 'vertical', minHeight: 60,
  },
  sectionLabel: {
    fontSize: 11, fontWeight: 700, letterSpacing: '0.07em',
    textTransform: 'uppercase', color: '#7b6b61', marginBottom: 10, marginTop: 16,
  },
  sectionBtn: {
    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: 8, background: '#f7f1ea', border: '1px solid #e8e0d5', borderRadius: 10,
    padding: '10px 12px', marginTop: 14, marginBottom: 10, cursor: 'pointer',
    fontFamily: 'inherit', fontSize: 12, fontWeight: 800, letterSpacing: '0.05em',
    textTransform: 'uppercase', color: '#5A4A42', minHeight: 44,
  },
  toggleRow: {
    display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10,
    fontSize: 13, color: '#2b1d14',
  },
  toggleCheckbox: { width: 16, height: 16, cursor: 'pointer', accentColor: '#5A4A42' },
  alert: { borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13 },
  alertError: { background: '#fde8e8', border: '1.5px solid #ef9a9a', color: '#8b0000' },
  alertWarn: { background: '#FEF3C7', border: '1px solid #FBBF24', color: '#B45309', borderRadius: 8, padding: '8px 12px', fontSize: 12.5, marginBottom: 10 },
  btnPrimary: {
    padding: '10px 20px', borderRadius: 10, border: 'none', cursor: 'pointer',
    fontWeight: 700, fontSize: 14, background: '#5A4A42', color: '#fff', lineHeight: 1,
    fontFamily: 'inherit', minHeight: 44,
  },
  btnSecondary: {
    padding: '10px 20px', borderRadius: 10, border: '1.5px solid #d5c9c0',
    cursor: 'pointer', fontWeight: 700, fontSize: 14, background: '#fff',
    color: '#5A4A42', lineHeight: 1, fontFamily: 'inherit', minHeight: 44,
  },
  btnRetry: {
    padding: '10px 20px', borderRadius: 10, border: '1.5px solid #ef9a9a',
    cursor: 'pointer', fontWeight: 700, fontSize: 14, background: '#fff',
    color: '#8b0000', lineHeight: 1, fontFamily: 'inherit', minHeight: 44,
  },
  loadingMsg: { color: '#7b6b61', fontSize: 14, padding: '12px 0' },
  unresolvedBox: {
    borderRadius: 10, padding: '16px 18px', background: '#fde8e8',
    border: '1.5px solid #ef9a9a', color: '#8b0000', fontSize: 13,
    marginBottom: 0,
  },
  unresolvedTitle: { fontWeight: 800, fontSize: 14, marginBottom: 6 },
  unresolvedHint: { marginTop: 8, fontSize: 12, color: '#5A4A42', lineHeight: 1.5 },
};

/**
 * Vendor settings, as a modal.
 *
 * This is the same set of settings the Vendors page edits, reachable from
 * wherever a vendor is blocking work — the purchase-order review most of all,
 * where "no email on file" stops an order dead. It opens on the field that
 * sent you here (`focusField`), so the fix is one tap away rather than a page
 * navigation and a hunt.
 *
 * Contact fields still go through `saveVendorContactAndPropagate`, the single
 * atomic path that also refreshes every open draft's contact snapshot. The
 * remaining settings are a plain vendor update, matching the Vendors page.
 * Renaming a vendor stays on the Vendors page, where alias rewriting lives.
 */
export default function VendorContactModal({ vendorId, vendorName, storeId, focusField = null, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const [error, setError] = useState('');
  const [vendorUnresolvable, setVendorUnresolvable] = useState(false);
  const [resolvedVendorId, setResolvedVendorId] = useState(vendorId || null);
  // The row as loaded, kept so fee provenance and "only what changed" can be
  // judged against it rather than against a blank form.
  const [vendorRow, setVendorRow] = useState(null);
  // False when the store is behind the migrations that added the ordering and
  // fee columns; the contact section still saves, the rest is hidden.
  const [settingsAvailable, setSettingsAvailable] = useState(true);
  const [openSections, setOpenSections] = useState({ contact: true, ordering: false, fees: false, business: false });
  const [form, setForm] = useState(EMPTY_FORM);
  // `vendors.fulfillment_type` is NOT NULL DEFAULT 'delivery', so writing it on
  // every save would silently answer the buying-mode question on the owner's
  // behalf — and they would then never be asked (see utils/buyingMode.js).
  // Only touching the control counts as an answer.
  const [fulfillmentTouched, setFulfillmentTouched] = useState(false);

  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    previousFocusRef.current = document.activeElement;
    return () => {
      if (previousFocusRef.current && typeof previousFocusRef.current.focus === 'function') {
        previousFocusRef.current.focus();
      }
    };
  }, []);

  // Land the caret where the caller pointed. A purchase order that cannot be
  // sent for want of an email opens this modal already in the email box.
  useEffect(() => {
    if (loading || !modalRef.current) return;
    if (focusField) {
      const target = modalRef.current.querySelector(`[data-field="${focusField}"]`);
      if (target && typeof target.focus === 'function') {
        target.focus();
        if (typeof target.select === 'function') target.select();
        return;
      }
    }
    const focusable = modalRef.current.querySelectorAll(FOCUSABLE);
    if (focusable.length > 0) focusable[0].focus();
  }, [loading, focusField]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (!saving) onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    if (!modalRef.current) return;
    const focusable = Array.from(modalRef.current.querySelectorAll(FOCUSABLE));
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, [onClose, saving]);

  useEffect(() => {
    let cancelled = false;

    // Full settings row when the columns exist, contact-only when they don't.
    async function fetchVendorRow(id) {
      const full = await supabase.from('vendors').select(SETTINGS_SELECT).eq('id', id).maybeSingle();
      if (!full.error) return { row: full.data, extended: true };
      const basic = await supabase.from('vendors').select(CONTACT_SELECT).eq('id', id).maybeSingle();
      if (basic.error) throw basic.error;
      console.warn('[VendorContactModal] extended vendor columns unavailable:', full.error.message);
      return { row: basic.data, extended: false };
    }

    async function load() {
      setLoading(true);
      setError('');
      setVendorUnresolvable(false);
      try {
        let vendor = null;
        let extended = true;

        // Fast path: look up by id when one is already on the PO.
        if (vendorId) {
          const result = await fetchVendorRow(vendorId);
          vendor = result.row;
          extended = result.extended;
        }

        // Slow path: resolve by name via the shared resolver.
        // This covers (a) exact display-name match, (b) alias match via
        // vendor_aliases, and (c) auto-create a stub row if nothing exists.
        // Runs whenever the fast path returned nothing — even when a vendorId
        // was supplied but no row matched (stale/RLS-filtered id).
        if (!vendor && vendorName && storeId != null) {
          const resolved = await resolveVendorByNameOrAlias({
            supabaseClient: supabase,
            storeId,
            vendorName,
            stubNote: 'Auto-created from vendor settings flow',
            createIfMissing: true,
          });
          if (resolved?.vendorId) {
            const result = await fetchVendorRow(resolved.vendorId);
            vendor = result.row;
            extended = result.extended;
          }
        }

        if (!vendor) {
          const msg = vendorName
            ? `Could not create or locate a vendor record for "${vendorName}".`
            : 'No vendor could be identified for this entry.';
          if (!cancelled) {
            setResolvedVendorId(null);
            setVendorUnresolvable(true);
            setError(msg);
            setLoading(false);
          }
          return;
        }

        if (!cancelled) {
          // Accept vendor.id as-is (UUID or numeric string from PostgREST).
          // Fall back to the prop vendorId if the row's id field is somehow
          // missing/invalid (defensive).
          const loadedId = vendor.id != null ? vendor.id : vendorId;
          const resolvedId = isValidVendorId(loadedId) ? loadedId : null;
          if (!resolvedId) {
            console.warn('[VendorContactModal] Loaded vendor row but id is not a valid id:', vendor);
          }
          setResolvedVendorId(resolvedId);
          setVendorRow(vendor);
          setSettingsAvailable(extended);
          setVendorUnresolvable(false);
          setForm({
            ...EMPTY_FORM,
            contact_name: vendor.contact_name || '',
            contact_email: vendor.contact_email || '',
            contact_phone: vendor.contact_phone || '',
            contact2_name: vendor.contact2_name || '',
            contact2_email: vendor.contact2_email || '',
            contact2_phone: vendor.contact2_phone || '',
            general_email: vendor.general_email || '',
            general_phone: vendor.general_phone || '',
            send_orders_to_contact_email: vendor.send_orders_to_contact_email !== false,
            send_orders_to_general_email: !!vendor.send_orders_to_general_email,
            fulfillment_type: vendor.fulfillment_type === 'pickup' ? 'pickup' : 'delivery',
            ordering_method: vendor.ordering_method || 'email',
            lead_days: vendor.lead_days ?? '',
            cutoff_time: (vendor.cutoff_time || '').slice(0, 5),
            delivery_days: vendor.delivery_days || '',
            minimum_order_amount: vendor.minimum_order_amount ?? '',
            below_minimum_fee: vendor.below_minimum_fee ?? '',
            delivery_fee: vendor.delivery_fee ?? '',
            free_delivery_threshold: vendor.free_delivery_threshold ?? '',
            fuel_surcharge_amount: vendor.fuel_surcharge_amount ?? '',
            sales_tax_rate: feeFractionToPercent(vendor.sales_tax_rate),
            account_number: vendor.account_number || '',
            payment_terms: vendor.payment_terms || '',
            address: vendor.address || '',
            website: vendor.website || '',
            notes: vendor.notes || '',
          });
          setError('');
          setLoading(false);
        }
      } catch (err) {
        console.warn('[VendorContactModal] load failed:', err);
        if (!cancelled) {
          setResolvedVendorId(null);
          setVendorUnresolvable(true);
          setError(err.message || 'Failed to load vendor.');
          setLoading(false);
        }
      }
    }
    load();
    return () => { cancelled = true; };
  }, [vendorId, vendorName, storeId, retryCount]);

  // Whichever section holds the field we were sent to opens with the modal.
  useEffect(() => {
    if (!focusField) return;
    const sectionOf = {
      contact_name: 'contact', contact_email: 'contact', contact_phone: 'contact',
      contact2_name: 'contact', contact2_email: 'contact', contact2_phone: 'contact',
      general_email: 'contact', general_phone: 'contact',
      fulfillment_type: 'ordering', ordering_method: 'ordering', lead_days: 'ordering',
      cutoff_time: 'ordering', delivery_days: 'ordering',
      minimum_order_amount: 'fees', below_minimum_fee: 'fees', delivery_fee: 'fees',
      free_delivery_threshold: 'fees', fuel_surcharge_amount: 'fees', sales_tax_rate: 'fees',
      account_number: 'business', payment_terms: 'business', address: 'business',
      website: 'business', notes: 'business',
    }[focusField];
    if (sectionOf) setOpenSections((prev) => ({ ...prev, [sectionOf]: true }));
  }, [focusField]);

  const set = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleSection = (name) => setOpenSections((prev) => ({ ...prev, [name]: !prev[name] }));

  const isValidId = isValidVendorId;

  const handleRetry = () => {
    setError('');
    setVendorUnresolvable(false);
    setRetryCount((c) => c + 1);
  };

  // The non-contact half of the form. Contact fields are excluded on purpose:
  // they ride the atomic RPC below so the vendor row and every open draft's
  // snapshot cannot end up disagreeing.
  const buildSettingsPatch = () => {
    if (!settingsAvailable) return null;
    const leadDays = Number(form.lead_days);
    const patch = {
      ordering_method: String(form.ordering_method || 'email').trim() || 'email',
      // smallint column — a typed "1.5" would be rejected outright.
      lead_days: Number.isFinite(leadDays) && String(form.lead_days).trim() !== ''
        ? Math.max(0, Math.round(leadDays))
        : 0,
      cutoff_time: String(form.cutoff_time || '').trim() || null,
      delivery_days: String(form.delivery_days || '').trim() || null,
      account_number: String(form.account_number || '').trim() || null,
      payment_terms: String(form.payment_terms || '').trim() || null,
      address: String(form.address || '').trim() || null,
      website: String(form.website || '').trim() || null,
      notes: String(form.notes || '').trim() || null,
    };
    // Fees follow the Vendors page rules exactly: blank means unknown (NULL),
    // an explicit 0 is a real answer, tax is typed as a percent and stored as
    // a fraction, and only columns the user actually changed ride the update
    // so a fee an invoice captured meanwhile is not reverted.
    const feeValues = {
      minimum_order_amount: parseFeeAmountInput(form.minimum_order_amount),
      below_minimum_fee: parseFeeAmountInput(form.below_minimum_fee),
      delivery_fee: parseFeeAmountInput(form.delivery_fee),
      free_delivery_threshold: parseFeeAmountInput(form.free_delivery_threshold),
      fuel_surcharge_amount: parseFeeAmountInput(form.fuel_surcharge_amount),
      sales_tax_rate: feePercentToFraction(form.sales_tax_rate),
    };
    Object.assign(patch, pickChangedFeeValues(vendorRow, feeValues));
    const feeProvenance = mergeFeeProvenance(vendorRow, feeValues);
    if (feeProvenance) patch.fee_provenance = feeProvenance;
    // Buying mode rides only when someone actually chose, and then it carries
    // its own provenance: who chose, and when.
    if (fulfillmentTouched) {
      patch.fulfillment_type = form.fulfillment_type === 'pickup' ? 'pickup' : 'delivery';
      patch.fulfillment_type_source = 'user';
      patch.fulfillment_type_set_at = new Date().toISOString();
    }
    return patch;
  };

  const handleSave = async () => {
    // Last-ditch resolve attempt: if the modal opened without a usable vendor
    // id but we have a name + store, try resolving on the fly so the click
    // still does something instead of failing with a confusing message.
    let effectiveVendorId = resolvedVendorId;
    if (!isValidId(effectiveVendorId) && vendorName && isValidId(storeId)) {
      try {
        const resolved = await resolveVendorByNameOrAlias({
          supabaseClient: supabase,
          storeId,
          vendorName,
          stubNote: 'Auto-created from vendor settings flow',
          createIfMissing: true,
        });
        if (resolved?.vendorId && isValidId(resolved.vendorId)) {
          effectiveVendorId = resolved.vendorId;
          setResolvedVendorId(effectiveVendorId);
        }
      } catch (resolveErr) {
        console.warn('[VendorContactModal] inline resolve failed:', resolveErr?.message);
      }
    }
    if (!isValidId(effectiveVendorId)) {
      setError('Vendor not resolved — cannot save. Try Retry, or open the Vendor Editor to assign a vendor.');
      return;
    }
    if (!isValidId(storeId)) {
      setError('Store not identified — cannot save.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      // Settings first, contact second. If the settings write fails the
      // contact write is not attempted, so the user is never told the email
      // was saved when half the form was rejected.
      const settingsPatch = buildSettingsPatch();
      if (settingsPatch?.fulfillment_type_source === 'user') {
        try {
          const { data: authData } = await supabase.auth.getUser();
          settingsPatch.fulfillment_type_set_by = authData?.user?.id ?? null;
        } catch (_) {
          settingsPatch.fulfillment_type_set_by = null;
        }
      }
      if (settingsPatch) {
        let { error: settingsErr } = await supabase
          .from('vendors')
          .update(settingsPatch)
          .eq('id', effectiveVendorId);
        if (settingsErr && isMissingFeeColumnError(settingsErr)) {
          const fallback = { ...settingsPatch };
          delete fallback.fuel_surcharge_amount;
          delete fallback.sales_tax_rate;
          delete fallback.fee_provenance;
          ({ error: settingsErr } = await supabase
            .from('vendors')
            .update(fallback)
            .eq('id', effectiveVendorId));
        }
        if (settingsErr) throw settingsErr;
      }

      const result = await saveVendorContactAndPropagate(effectiveVendorId, storeId, {
        contact_name: form.contact_name || null,
        contact_email: form.contact_email || null,
        contact_phone: form.contact_phone || null,
        contact2_name: form.contact2_name || null,
        contact2_email: form.contact2_email || null,
        contact2_phone: form.contact2_phone || null,
        general_email: form.general_email || null,
        general_phone: form.general_phone || null,
        send_orders_to_contact_email: form.send_orders_to_contact_email,
        send_orders_to_general_email: form.send_orders_to_general_email,
      });

      const updatedCount = result?.updatedCount || 0;
      setSaving(false);
      setSavedCount(updatedCount);
      setSaved(true);
      // Notify sidebar/mobile-nav to re-check the vendor-missing-contact signal.
      try { window.dispatchEvent(new CustomEvent('dashboard-vendors-changed')); } catch (_) {}
      setTimeout(() => {
        if (onSaved) onSaved(result?.updatedVendor || null, effectiveVendorId, updatedCount);
      }, 1000);
    } catch (err) {
      console.warn('[VendorContactModal] save failed:', err);
      setError(err?.message || 'Save failed. Please try again.');
      setSaving(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (saving) return;
    if (e.target === e.currentTarget) onClose();
  };

  // Keep the Save button clickable whenever we're not actively saving/saved/loading.
  // handleSave does the real validation and surfaces a specific in-modal error
  // instead of leaving the user staring at a silently-disabled button.
  const canSave = !saving && !saved && !loading;

  useHideMobileNav(true);

  const field = (name, label, { type = 'text', placeholder = '', hint = '', wide = false, area = false } = {}) => (
    <div style={wide ? S.fieldGroup : S.fieldCol} key={name}>
      <label style={S.label} htmlFor={`vcm-${name}`}>{label}</label>
      {area ? (
        <textarea
          id={`vcm-${name}`}
          data-field={name}
          style={S.textarea}
          value={form[name]}
          onChange={set(name)}
          placeholder={placeholder}
          disabled={saving}
          rows={2}
        />
      ) : (
        <input
          id={`vcm-${name}`}
          data-field={name}
          style={S.input}
          type={type}
          inputMode={type === 'number' ? 'decimal' : undefined}
          min={type === 'number' ? '0' : undefined}
          step={type === 'number' ? '0.01' : undefined}
          value={form[name]}
          onChange={set(name)}
          placeholder={placeholder}
          disabled={saving}
        />
      )}
      {hint && <div style={S.hint}>{hint}</div>}
    </div>
  );

  const sectionHeader = (name, label) => (
    <button type="button" style={S.sectionBtn} onClick={() => toggleSection(name)} aria-expanded={openSections[name]}>
      <span>{label}</span>
      <span style={{ fontSize: 11 }}>{openSections[name] ? '▲' : '▼'}</span>
    </button>
  );

  return (
    <div
      style={S.overlay}
      onClick={handleOverlayClick}
      role="presentation"
    >
      <div
        ref={modalRef}
        style={S.modal}
        role="dialog"
        aria-modal="true"
        aria-label={`Vendor settings for ${vendorName || 'vendor'}`}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        <div style={S.header}>
          <div>
            <div style={S.title}>Vendor Contact Information</div>
            <div style={S.subtitle}>{vendorRow?.display_name || vendorName || 'Vendor settings'}</div>
          </div>
          <button style={{ ...S.closeBtn, opacity: saving ? 0.4 : 1 }} onClick={() => { if (!saving) onClose(); }} aria-label="Close" disabled={saving}>×</button>
        </div>

        <div style={S.body}>
          {saved ? (
            <div style={{ ...S.alert, background: '#e8f5e9', border: '1.5px solid #66bb6a', color: '#1b5e20', marginBottom: 0 }}>
              <strong>Vendor saved!</strong>
              {savedCount > 0
                ? ` Updated ${savedCount} open draft${savedCount !== 1 ? 's' : ''} with the new contact info.`
                : ''}
            </div>
          ) : loading ? (
            <div style={S.loadingMsg}>{retryCount > 0 ? 'Retrying…' : 'Loading vendor…'}</div>
          ) : vendorUnresolvable ? (
            <div style={S.unresolvedBox}>
              <div style={S.unresolvedTitle}>Vendor could not be found</div>
              <div>{error || 'This vendor record could not be located or created.'}</div>
              <div style={S.unresolvedHint}>
                {vendorName && storeId != null
                  ? 'You can retry — or open the Vendor Editor to create or link a vendor record manually, then come back here.'
                  : 'No vendor name or store is available for this entry. Open the Vendor Editor to assign a vendor, then try again.'}
              </div>
            </div>
          ) : (
            <>
              {error && <div style={{ ...S.alert, ...S.alertError }}>{error}</div>}

              {sectionHeader('contact', 'Contact information')}
              {openSections.contact && (
                <>
                  <div style={S.sectionLabel}>Primary contact</div>
                  <div style={S.fieldRow}>
                    {field('contact_name', 'Contact Name', { placeholder: 'e.g. Jane Smith' })}
                    {field('contact_phone', 'Phone', { type: 'tel', placeholder: '(555) 010-1234' })}
                  </div>
                  {field('contact_email', 'Contact Email', { type: 'email', placeholder: 'orders@vendor.com', wide: true })}
                  <div style={S.toggleRow}>
                    <input
                      id="vcm-toggle-contact"
                      style={S.toggleCheckbox}
                      type="checkbox"
                      checked={form.send_orders_to_contact_email}
                      onChange={set('send_orders_to_contact_email')}
                      disabled={saving}
                    />
                    <label htmlFor="vcm-toggle-contact" style={{ cursor: 'pointer' }}>
                      Send purchase orders to this email
                    </label>
                  </div>

                  <div style={S.sectionLabel}>Secondary contact</div>
                  <div style={S.fieldRow}>
                    {field('contact2_name', 'Secondary Contact Name', { placeholder: 'e.g. John Doe' })}
                    {field('contact2_phone', 'Phone', { type: 'tel', placeholder: '(555) 010-5678' })}
                  </div>
                  {field('contact2_email', 'Secondary Contact Email', { type: 'email', placeholder: 'backup@vendor.com', wide: true })}

                  <div style={S.sectionLabel}>General / shared</div>
                  <div style={S.fieldRow}>
                    {field('general_email', 'General Email', { type: 'email', placeholder: 'info@vendor.com' })}
                    {field('general_phone', 'General Phone', { type: 'tel', placeholder: '(555) 010-9999' })}
                  </div>
                  <div style={S.toggleRow}>
                    <input
                      id="vcm-toggle-general"
                      style={S.toggleCheckbox}
                      type="checkbox"
                      checked={form.send_orders_to_general_email}
                      onChange={set('send_orders_to_general_email')}
                      disabled={saving}
                    />
                    <label htmlFor="vcm-toggle-general" style={{ cursor: 'pointer' }}>
                      Send purchase orders to this email
                    </label>
                  </div>
                  {!form.send_orders_to_contact_email && !form.send_orders_to_general_email && (
                    <div style={S.alertWarn}>
                      Tick at least one box above so purchase orders have somewhere to be sent.
                    </div>
                  )}
                </>
              )}

              {settingsAvailable && (
                <>
                  {sectionHeader('ordering', 'Ordering')}
                  {openSections.ordering && (
                    <>
                      <div style={S.fieldRow}>
                        <div style={S.fieldCol}>
                          <label style={S.label} htmlFor="vcm-fulfillment_type">How do you buy from this vendor?</label>
                          <select
                            id="vcm-fulfillment_type"
                            data-field="fulfillment_type"
                            style={S.input}
                            value={form.fulfillment_type}
                            onChange={(e) => { setFulfillmentTouched(true); set('fulfillment_type')(e); }}
                            disabled={saving}
                          >
                            <option value="delivery">They deliver to us</option>
                            <option value="pickup">We pick it up</option>
                          </select>
                        </div>
                        <div style={S.fieldCol}>
                          <label style={S.label} htmlFor="vcm-ordering_method">Ordering method</label>
                          <select
                            id="vcm-ordering_method"
                            data-field="ordering_method"
                            style={S.input}
                            value={form.ordering_method}
                            onChange={set('ordering_method')}
                            disabled={saving}
                          >
                            <option value="email">Email</option>
                            <option value="phone">Phone</option>
                            <option value="portal">Online portal</option>
                            <option value="text">Text message</option>
                            <option value="in_person">In person</option>
                          </select>
                        </div>
                      </div>
                      <div style={S.fieldRow}>
                        {field('lead_days', 'Lead time (days)', { type: 'number', placeholder: '0' })}
                        <div style={S.fieldCol}>
                          <label style={S.label} htmlFor="vcm-cutoff_time">Order cutoff time</label>
                          <input
                            id="vcm-cutoff_time"
                            data-field="cutoff_time"
                            style={S.input}
                            type="time"
                            value={form.cutoff_time}
                            onChange={set('cutoff_time')}
                            disabled={saving}
                          />
                        </div>
                      </div>
                      {field('delivery_days', 'Delivery days', {
                        placeholder: 'e.g. Mon, Wed, Fri',
                        hint: 'The days this vendor normally delivers — the days they run, not the date of one order.',
                        wide: true,
                      })}
                    </>
                  )}

                  {sectionHeader('fees', 'Fees & charges')}
                  {openSections.fees && (
                    <>
                      <div style={{ ...S.hint, marginTop: 0, marginBottom: 8 }}>
                        Filled automatically from invoices when blank — your edits always win.
                      </div>
                      <div style={S.fieldRow}>
                        {field('minimum_order_amount', 'Minimum order ($)', { type: 'number' })}
                        {field('below_minimum_fee', 'Small-order fee ($)', { type: 'number', hint: 'Charged under the minimum' })}
                      </div>
                      <div style={S.fieldRow}>
                        {field('delivery_fee', 'Delivery fee ($)', { type: 'number' })}
                        {field('free_delivery_threshold', 'Free delivery over ($)', { type: 'number' })}
                      </div>
                      <div style={S.fieldRow}>
                        {field('fuel_surcharge_amount', 'Fuel surcharge ($)', { type: 'number' })}
                        {field('sales_tax_rate', 'Sales tax rate (%)', { type: 'number', hint: 'e.g. 8.25 for 8.25%' })}
                      </div>
                    </>
                  )}

                  {sectionHeader('business', 'Business details')}
                  {openSections.business && (
                    <>
                      <div style={S.fieldRow}>
                        {field('account_number', 'Account number', { placeholder: 'Your account with them' })}
                        {field('payment_terms', 'Payment terms', { placeholder: 'e.g. Net 30' })}
                      </div>
                      <div style={S.fieldRow}>
                        {field('website', 'Website', { type: 'url', placeholder: 'https://…' })}
                      </div>
                      {field('address', 'Address', { area: true, placeholder: 'Street, city, state', wide: true })}
                      {field('notes', 'Notes', { area: true, placeholder: 'Anything the next person ordering should know', wide: true })}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div style={S.footer}>
          <button style={S.btnSecondary} onClick={onClose} disabled={saving || saved}>
            {vendorUnresolvable ? 'Close' : 'Cancel'}
          </button>
          {vendorUnresolvable ? (
            (vendorName && storeId != null) && (
              <button style={S.btnRetry} onClick={handleRetry}>
                Retry
              </button>
            )
          ) : (
            <button
              style={{ ...S.btnPrimary, opacity: canSave ? 1 : 0.7 }}
              onClick={handleSave}
              disabled={!canSave}
            >
              {saving ? 'Saving…' : saved ? 'Saved!' : 'Save Vendor'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
